/**
 * Hybrid root loader with a tab-scoped SWR cache.
 * Auth data is fetched fresh on every call and merged over cached root data.
 */

import type {
  ClientLoaderFunction,
  ClientLoaderFunctionArgs,
} from "react-router";
import { parseURL } from "ufo";

import { SiteSdk } from "~/core/site-sdk";
import type { loader } from "~/root";
import type { ParsedAuthCookieData } from "~/routes/api.auth.me";
import { normalizeUrl } from "~/shared/utils/normalize-url";
import { parseConditionTemplate } from "~/shared/utils/parse-condition-template";
import { evaluateCmsExpression } from "~/shared/utils/parse-string-expression-to-boolean";
import { ROOM_URL_TEMPLATES } from "~/shared/utils/parse-url-template-and-build-href";

type RootServerData = Awaited<ReturnType<typeof loader>>;
const cache = new Map<string, RootServerData>();

function deriveCacheKey(url: string): string {
  return url;
}

const getSafeAuthData = async (
  siteSdk: SiteSdk,
): Promise<ParsedAuthCookieData> => {
  try {
    const authData = await siteSdk.getUserAuthData();
    if (typeof authData !== "object" || authData === null) {
      return {};
    }
    return authData;
  } catch {
    return {};
  }
};

async function processRedirectAndMerge(
  serverData: RootServerData,
  authData: ParsedAuthCookieData,
  siteSdk: SiteSdk,
  requestUrl: string,
): Promise<RootServerData & ParsedAuthCookieData> {
  if (!serverData.redirectForClient) {
    return { ...serverData, ...authData };
  }
  const redirectForClientEntity =
    serverData?.redirectForClient?.[0]?.redirect_entity_id;
  if (!redirectForClientEntity) return { ...serverData, ...authData };

  let parsedCondition: boolean | string | number = false;
  try {
    parsedCondition = evaluateCmsExpression(
      parseConditionTemplate(redirectForClientEntity.condition ?? "", {
        AUTH_TOKEN: String(!!authData.authorizationToken),
        IS_NEW_USER: String(false),
      }),
    );
  } catch {
    parsedCondition = false;
  }

  if (parsedCondition) {
    let preparedHrefTo: string | undefined;

    try {
      if (redirectForClientEntity?.to?.includes(ROOM_URL_TEMPLATES.ROOM_URL)) {
        const template = redirectForClientEntity?.to;
        const locale = serverData?.language;

        if (template && locale) {
          const { redirectUrl } = await siteSdk.getParsedRoomUrlFromTemplate({
            locale,
            template,
          });
          preparedHrefTo = redirectUrl;
        }
      } else {
        const query = redirectForClientEntity?.preserve_query
          ? parseURL(requestUrl).search
          : "";
        preparedHrefTo =
          normalizeUrl(redirectForClientEntity?.to ?? "") + query;
      }
    } catch {
      preparedHrefTo = undefined;
    }

    if (preparedHrefTo) {
      window.location.replace(preparedHrefTo);
    }
  }
  return { ...serverData, ...authData };
}

export const rootClientLoader = (async ({
  serverLoader,
  request,
}: ClientLoaderFunctionArgs) => {
  const key = deriveCacheKey(request.url);
  const siteSdk = new SiteSdk();

  const cached = cache.get(key);
  if (cached !== undefined) {
    void serverLoader<typeof loader>()
      .then((fresh) => {
        cache.set(key, fresh);
      })
      .catch(() => {
        // Ignore background refresh failures; stale data has already been returned.
      });

    const authData = await getSafeAuthData(siteSdk);
    return processRedirectAndMerge(cached, authData, siteSdk, request.url);
  }

  const serverData = await serverLoader<typeof loader>();
  cache.set(key, serverData);
  const authData = await getSafeAuthData(siteSdk);

  return processRedirectAndMerge(serverData, authData, siteSdk, request.url);
}) satisfies ClientLoaderFunction;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
rootClientLoader.hydrate = true as const;
